// Line icons in the launcher's style (it draws gpui-kit's Lucide set: launcher/src/ui.rs rail and top bar). Built with
// createElementNS from fixed path data, never from strings that carry data.

const SVG = "http://www.w3.org/2000/svg";

type Shape = ["path", string] | ["circle", number, number, number] | ["rect", number, number, number, number, number] | ["polygon", string];

const ICONS: Record<string, Shape[]> = {
  home: [
    ["rect", 3, 3, 7, 9, 1],
    ["rect", 14, 3, 7, 5, 1],
    ["rect", 14, 12, 7, 9, 1],
    ["rect", 3, 16, 7, 5, 1],
  ],
  browse: [
    ["circle", 12, 12, 10],
    ["path", "m16.24 7.76-1.8 5.41a2 2 0 0 1-1.27 1.27L7.76 16.24l1.8-5.41a2 2 0 0 1 1.27-1.27z"],
  ],
  assets: [
    ["path", "M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"],
    ["path", "m3.3 7 8.7 5 8.7-5"],
    ["path", "M12 22V12"],
  ],
  events: [
    ["rect", 3, 4, 18, 18, 2],
    ["path", "M16 2v4M8 2v4M3 10h18"],
  ],
  feed: [
    ["path", "M4 11a9 9 0 0 1 9 9"],
    ["path", "M4 4a16 16 0 0 1 16 16"],
    ["circle", 5, 19, 1],
  ],
  download: [
    ["path", "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"],
    ["path", "m7 10 5 5 5-5"],
    ["path", "M12 15V3"],
  ],
  create: [
    ["path", "m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z"],
    ["path", "m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65"],
    ["path", "m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65"],
  ],
  search: [
    ["circle", 11, 11, 8],
    ["path", "m21 21-4.3-4.3"],
  ],
  play: [["polygon", "6 3 20 12 6 21 6 3"]],
  left: [["path", "m15 18-6-6 6-6"]],
  right: [["path", "m9 18 6-6-6-6"]],
  monitor: [
    ["rect", 2, 3, 20, 14, 2],
    ["path", "M8 21h8M12 17v4"],
  ],
  copy: [
    ["rect", 8, 8, 14, 14, 2],
    ["path", "M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"],
  ],
  check: [["path", "M20 6 9 17l-5-5"]],
  library: [
    ["path", "M2 7v11a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7"],
    ["rect", 2, 3, 20, 4, 1],
    ["path", "M10 12h4"],
  ],
  users: [
    ["path", "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"],
    ["circle", 9, 7, 4],
    ["path", "M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"],
  ],
  shield: [["path", "M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"]],
  terminal: [["path", "m4 17 6-6-6-6M12 19h8"]],
};

export type IconName = keyof typeof ICONS;

export function icon(name: IconName, size = 18): SVGSVGElement {
  const svg = document.createElementNS(SVG, "svg");
  for (const [k, v] of Object.entries({ width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": 2, "stroke-linecap": "round", "stroke-linejoin": "round", "aria-hidden": "true", class: "icon" })) {
    svg.setAttribute(k, String(v));
  }
  for (const s of ICONS[name] ?? []) {
    const el = document.createElementNS(SVG, s[0]);
    if (s[0] === "path") el.setAttribute("d", s[1]);
    else if (s[0] === "polygon") el.setAttribute("points", s[1]);
    else if (s[0] === "circle") [["cx", s[1]], ["cy", s[2]], ["r", s[3]]].forEach(([a, v]) => el.setAttribute(String(a), String(v)));
    else [["x", s[1]], ["y", s[2]], ["width", s[3]], ["height", s[4]], ["rx", s[5]]].forEach(([a, v]) => el.setAttribute(String(a), String(v)));
    svg.appendChild(el);
  }
  return svg;
}
